import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import { AppLayout, TopNavigation, SideNavigation } from '@cloudscape-design/components';
import { useAuth } from './hooks/useAuth';
import Auth from './components/auth/Auth';
import FundingRequestTracker from './pages/FundingRequestTracker';
import FundingRequestDetail from './pages/FundingRequestDetail';
import FeedbackReport from './pages/FeedbackReport';
import AuditReport from './pages/AuditReport';
import SubmitFeedback from './pages/SubmitFeedback';
import { logVisit } from './services/s3Service';
import CalculatorCheck from './pages/CalculatorCheck';

import { isFundRequestDetailsPage } from './utils/urlHelper';
import { defaultLandingHref, isCalculatorOnlyUser } from './utils/permissions';
import Breadcrumbs from './components/Breadcrumbs';
import ValidationTest from './pages/ValidationTest';



const getEnvironment = () => {
  if (window._env_?.ENVIRONMENT) return window._env_.ENVIRONMENT;
  if (window.APP_CONFIG?.ENVIRONMENT) return window.APP_CONFIG.ENVIRONMENT;
  return 'prod'; // fail-closed: hide alpha features if config is missing
};

function MainApp() {
  const { isAuthenticated, user, loading: authLoading, login, logout } = useAuth();
  const location = useLocation();
  const isDetailsPage = isFundRequestDetailsPage();
  const [navigationOpen, setNavigationOpen] = useState(() => !isDetailsPage);
  const [navigationOpenBeforeDetails, setNavigationOpenBeforeDetails] = useState(true);
  const [previousIsDetailsPage, setPreviousIsDetailsPage] = useState(isDetailsPage);
  const history = useHistory();

  // Log visit once per browser session
  useEffect(() => {
    if (isAuthenticated && !sessionStorage.getItem('visit_logged')) {
      sessionStorage.setItem('visit_logged', 'true');
      logVisit();
    }
  }, [isAuthenticated]);

  // Adjust during render so Fund Request details commit collapsed without
  // replacing the user's navigation choice on non-detail pages.
  if (isDetailsPage !== previousIsDetailsPage) {
    setPreviousIsDetailsPage(isDetailsPage);
    if (isDetailsPage) {
      setNavigationOpenBeforeDetails(navigationOpen);
      setNavigationOpen(false);
    } else {
      setNavigationOpen(navigationOpenBeforeDetails);
    }
  }

  if (authLoading) {
    return null;
  }

  // FIXED: Don't auto-call login() here - let useAuth handle redirects
  // Allow /auth route to render even when not authenticated
  if (!isAuthenticated && location.pathname !== '/auth') {
    return null;
  }

  // P449942577: ChronosGeneral tier is calculator-only. UI affordance only
  // — real authorization is enforced backend via can_validate / programs
  // (see ChronosLambdas shared/permissions.py). See isCalculatorOnlyUser
  // for the derivation rationale.
  const isCalculatorOnly = isCalculatorOnlyUser(user);
  const landingHref = defaultLandingHref(user);

  const handleChronosClick = (e) => {
    e.preventDefault();
    // Logo / "Chronos" header click goes to the user's default landing
    // page — calculator-only users land on /calculator-check, everyone
    // else on /dashboard. Pre-fix this always pushed /dashboard, which
    // sent calc-only users to a Fund Requests view the backend 403s.
    history.push(landingHref);
  };


  // Active-href fallback must reference isCalculatorOnly so the calc-only
  // nav highlights /calculator-check (the only "home" tab in that nav)
  // rather than /dashboard, which isn't even rendered as a link for them.
  // AutoSDE flagged this as the active-tab not lighting up issue.
  const getActiveHref = () => {
    const { pathname } = location;
    if (isDetailsPage) return pathname;
    if (pathname === '/calculator-check') return '/calculator-check';
    if (pathname === '/event-history') return '/event-history';
    if (pathname === '/reporting') return '/reporting';
    if (pathname === '/submit-feedback') return '/submit-feedback';
    return isCalculatorOnly ? '/calculator-check' : '/dashboard';
  };

  const toolsAndResourcesSection = {
    type: 'section',
    text: 'Tools & Resources',
    items: [
      {
        type: 'link',
        text: 'MAP Included Services List',
        href: 'https://s3-us-west-2.amazonaws.com/map-2.0-customer-documentation/included-services/MAP_Included_Services_List.pdf',
        external: true
      },
      {
        type: 'link',
        text: 'Connect Sales Specialist',
        href: 'https://river.amazon.com/ATL19/workflows?buildingType=AMZN_CONNECT&q0=23286081-a445-481f-b2d2-e42ae3969101&id=23286081-a445-481f-b2d2-e42ae3969101',
        external: true
      }
    ]
  };

  const linksOfInterestSection = {
    type: 'section',
    text: 'Links of interest',
    items: [
      { type: 'link', text: 'About Chronos', href: 'https://w.amazon.com/bin/view/MigrationPSATeam/Chronos/', external: true },
      { type: 'link', text: 'User Guide', href: 'https://amazon.awsapps.com/workdocs-amazon/index.html#/document/0910ebe7498593e5872ac4f77d6d5cd5402c2b73da4756467edc25a2778199c0', external: true },
      { type: 'link', text: '#chronos-interest', href: 'https://amazon.enterprise.slack.com/archives/C0AHD16JZ7B', external: true }
    ]
  };

  const navigationItems = isCalculatorOnly
    ? [
        { type: 'link', text: 'Review Calculator', href: '/calculator-check' },
        { type: 'link', text: 'Submit Feedback', href: '/submit-feedback' },
        { type: 'divider' },
        toolsAndResourcesSection,
        linksOfInterestSection,
      ]
    : [
        { type: 'link', text: 'Fund Requests', href: '/dashboard' },
        { type: 'link', text: 'Review Calculator', href: '/calculator-check' },
        { type: 'link', text: 'Event History', href: '/event-history' },
        { type: 'link', text: 'Reporting', href: '/reporting' },
        { type: 'link', text: 'Submit Feedback', href: '/submit-feedback' },
        { type: 'divider' },
        toolsAndResourcesSection,
        linksOfInterestSection,
        ...(getEnvironment() === 'alpha' ? [
          { type: 'divider' },
          {
            type: 'section',
            text: 'Test New Program',
            items: [
              { type: 'link', text: 'Validate Agent', href: '/validation-test' },
            ]
          }
        ] : [])
      ];

  return (
    <>
      <TopNavigation
        identity={{
          href: '#',
          title: 'Chronos',
          logo: {
            src: '/logo.png',
            alt: 'Chronos Logo',
          },
          onFollow: handleChronosClick
        }}
        utilities={[
          {
            type: 'button',
            text: `${user?.isAdmin ? 'Admin / ' : ''}${user?.email ? user.email.split('@')[0] : user?.name || 'User'}`,
            description: user?.email || '',
            iconName: 'user-profile'
          },
          {
            type: 'button',
            text: 'Refresh Credentials',
            onClick: logout
          }
        ]}
      />
      <AppLayout
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={
          <SideNavigation
            activeHref={getActiveHref()}
            header={{ text: 'Chronos', href: landingHref }}
            items={navigationItems}
            onFollow={event => {
              if (event.detail.external) {
                return;
              }
              event.preventDefault();
              const href = event.detail.href;
              history.push(href);
            }}
          />
        }
        toolsHide
        contentType="default"
        maxContentWidth={Number.MAX_VALUE}
        breadcrumbs={<Breadcrumbs />}
        content={
          <Switch>
            <Route exact path="/auth">
              <Auth />
            </Route>
            <Route exact path="/dashboard">
              {/* Single guarded redirect: calc-only users hitting
                  /dashboard (post-auth landing, browser back, bookmarks,
                  the catch-all redirect below) are punted to the
                  calculator. Backend would 403 every API call from this
                  page anyway; this keeps the user oriented to the one
                  surface they actually have access to. */}
              {isCalculatorOnly ? <Redirect to="/calculator-check" /> : <FundingRequestTracker />}
            </Route>
            <Route exact path="/fund-requests/:id/details">
              <FundingRequestDetail />
            </Route>
            <Route exact path="/event-history">
              <AuditReport />
            </Route>
            <Route exact path="/reporting">
              <FeedbackReport />
            </Route>
            <Route exact path="/submit-feedback">
              <SubmitFeedback />
            </Route>
            <Route exact path="/calculator-check">
              <CalculatorCheck />
            </Route>
            {getEnvironment() === 'alpha' && (
              <Route exact path="/validation-test">
                <ValidationTest />
              </Route>
            )}
            <Route path="*">
              <Redirect to="/dashboard" />
            </Route>
          </Switch>
        }
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <MainApp />
    </Router>
  );
}

export default App;
